import React, { useState, useMemo } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CallMadeIcon from "@mui/icons-material/CallMade";
import OutputOutlinedIcon from "@mui/icons-material/OutputOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useStatusPalette } from "../../../utils/statusColors";
import MarkdownContent from "../MarkdownContent";
import { parseAttachedFiles, MessageAttachmentTiles } from "../AttachmentStrip";
import AgentAvatar from "../AgentAvatar";
import {
  AVATAR_SIZE,
  AVATAR_RADIUS,
  TEXT_INDENT,
} from "./conversationConstants";
import {
  messageRowSx,
  formatMessageTime,
  getAgentAvatarClickHandler,
  getClickableAgentAvatarSx,
  buildSupervisorDecisionConfig,
  parseSupervisorDecisionContent,
} from "./conversationUtils";
import { AgentDetailPopover, CorrelationIdBadge } from "./InterAgentMessages";

export function SendMessageCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  awaitingResponse,
  onOpenAgentSettings,
}) {
  const _sp = useStatusPalette();
  const [expanded, setExpanded] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  let targetAgentName = null;
  let targetAgentId = null;
  let targetSessionId = null;
  let messagePreview = null;
  let correlationId = null;

  try {
    const parsed = typeof args === "string" ? JSON.parse(args) : args;
    if (parsed) {
      targetAgentId = parsed.targetAgentId;
      messagePreview = parsed.message;
      targetSessionId = parsed.targetChatSessionId;
      if (parsed.correlationId) correlationId = parsed.correlationId;
    }
  } catch {}

  try {
    const parsedResult =
      typeof result === "string" ? JSON.parse(result) : result;
    if (parsedResult) {
      targetAgentName = parsedResult.targetAgentName;
      if (!targetSessionId) targetSessionId = parsedResult.targetChatSessionId;
      if (!correlationId && parsedResult.correlationId)
        correlationId = parsedResult.correlationId;
    }
  } catch {}

  const displayTargetName = targetAgentName || "Unknown Agent";
  const targetHref = targetAgentId
    ? targetSessionId
      ? `/agents/${targetAgentId}?sessionId=${targetSessionId}`
      : `/agents/${targetAgentId}`
    : null;

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: "action.hover",
              cursor: "pointer",
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <CallMadeIcon sx={{ fontSize: 14, color: "primary.main" }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "text.secondary",
                flex: 1,
              }}
            >
              To:{" "}
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  setPopoverAnchor(e.currentTarget);
                }}
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {displayTargetName}
              </Box>
            </Typography>
            {targetAgentId && (
              <AgentDetailPopover
                anchorEl={popoverAnchor}
                open={Boolean(popoverAnchor)}
                onClose={() => setPopoverAnchor(null)}
                agentId={targetAgentId}
              />
            )}
            {correlationId && (
              <CorrelationIdBadge correlationId={correlationId} />
            )}
            {targetHref && (
              <Box
                component="a"
                href={targetHref}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "primary.main",
                  textDecoration: "none",
                  flexShrink: 0,
                  transition: "all 100ms ease",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    borderColor: "divider",
                  },
                }}
              >
                Open
                <OpenInNewIcon sx={{ fontSize: 11 }} />
              </Box>
            )}
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                color: "text.secondary",
                transition: "transform 0.2s",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
          {messagePreview && !expanded && (
            <Box sx={{ px: 1.5, py: 0.75, borderTop: 1, borderColor: "divider" }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  lineHeight: 1.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {messagePreview}
              </Typography>
            </Box>
          )}
          <Collapse in={expanded}>
            {messagePreview && (
              <Box sx={{ borderTop: 1, borderColor: "divider", px: 1.5, py: 1 }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "text.secondary",
                    mb: 0.5,
                  }}
                >
                  Message
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "text.primary",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {messagePreview}
                </Typography>
              </Box>
            )}
            {result != null && (
              <Box sx={{ borderTop: 1, borderColor: "divider" }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "text.secondary",
                    px: 1.5,
                    pt: 0.75,
                    pb: 0.25,
                  }}
                >
                  Response
                </Typography>
                <Box
                  sx={{
                    mx: 1.5,
                    mb: 1,
                    p: 1,
                    backgroundColor: "action.hover",
                    borderRadius: "6px",
                    maxHeight: "120px",
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      fontSize: "11px",
                      fontFamily: "monospace",
                      color: "text.secondary",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      m: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {typeof result === "string"
                      ? result
                      : JSON.stringify(result, null, 2)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Collapse>
          {awaitingResponse && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderTop: 1,
                borderColor: "divider",
                backgroundColor: _sp.amber.bg,
              }}
            >
              <CircularProgress
                size={12}
                thickness={5}
                sx={{ color: _sp.amber.color }}
              />
              <Typography
                sx={{ fontSize: "11px", fontWeight: 500, color: _sp.amber.color }}
              >
                Awaiting response...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function MessageImageThumbnails({ imageUrls }) {
  if (!imageUrls || imageUrls.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
      {imageUrls.map((url, i) => (
        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
          <Box
            component="img"
            src={url}
            alt={`Uploaded image ${i + 1}`}
            sx={{
              maxWidth: 180,
              maxHeight: 180,
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
              objectFit: "cover",
              cursor: "pointer",
              "&:hover": { opacity: 0.85 },
            }}
          />
        </a>
      ))}
    </Box>
  );
}

export function UserMessage({
  content,
  timestamp,
  grouped,
  senderName,
  messageChannel,
  imageUrls,
}) {
  const _sp = useStatusPalette();
  const isEmailMessage = messageChannel === "EMAIL";
  const displayName = senderName || "You";
  const isNamedUser = senderName && senderName !== "You";

  const { text: displayText, files: attachments } = useMemo(
    () => parseAttachedFiles(content),
    [content]
  );
  const hasFiles = attachments.length > 0;

  if (grouped) {
    return (
      <Box sx={{ ...messageRowSx, py: 0.4 }}>
        <Box sx={{ pl: TEXT_INDENT }}>
          {hasFiles && <MessageAttachmentTiles files={attachments} />}
          <MessageImageThumbnails imageUrls={imageUrls} />
          {isEmailMessage ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "flex-start",
                gap: 0.75,
                px: 1,
                py: 0.5,
                borderRadius: "8px",
                border: `1px solid ${_sp.amberAlt.border}`,
                backgroundColor: _sp.amberAlt.bg,
                maxWidth: "80%",
              }}
            >
              <EmailOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: _sp.amber.color,
                  mt: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  color: _sp.amberAlt.color,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {displayText}
              </Typography>
            </Box>
          ) : displayText ? (
            <Typography
              sx={{
                fontSize: "14px",
                color: "text.primary",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {displayText}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ ...messageRowSx, pt: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <AgentAvatar
          name={isNamedUser ? senderName : null}
          size={AVATAR_SIZE}
          {...(!isNamedUser ? { children: "Y", sx: { bgcolor: "text.primary" } } : {})}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "text.primary",
                lineHeight: 1.4,
              }}
            >
              {displayName}
            </Typography>
            {isEmailMessage && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: "6px",
                  height: "18px",
                  borderRadius: "5px",
                  backgroundColor: _sp.amberAlt.bg,
                  border: `1px solid ${_sp.amberAlt.border}`,
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 11, color: _sp.amber.color }} />
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: _sp.amber.color,
                    lineHeight: 1,
                  }}
                >
                  Email
                </Typography>
              </Box>
            )}
            {timestamp && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.disabled",
                  lineHeight: 1.4,
                  flexShrink: 0,
                }}
              >
                {formatMessageTime(timestamp)}
              </Typography>
            )}
          </Box>
          {hasFiles && (
            <Box sx={{ mt: 0.5 }}>
              <MessageAttachmentTiles files={attachments} />
            </Box>
          )}
          <MessageImageThumbnails imageUrls={imageUrls} />
          {isEmailMessage ? (
            <Box
              sx={{
                mt: 0.4,
                px: 1.25,
                py: 0.75,
                borderRadius: "8px",
                border: `1px solid ${_sp.amberAlt.border}`,
                backgroundColor: _sp.amberAlt.bg,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: _sp.amberAlt.color,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {displayText}
              </Typography>
            </Box>
          ) : displayText ? (
            <Typography
              sx={{
                fontSize: "14px",
                color: "text.primary",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                mt: 0.25,
              }}
            >
              {displayText}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

export function AgentMessage({
  content,
  agentName,
  agentId,
  timestamp,
  grouped,
  isStreaming,
  onOpenAgentSettings,
}) {
  const name = agentName || "Agent";
  const displayContent = content;
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);

  const streamingCursor = isStreaming ? (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: "2px",
        height: "1em",
        backgroundColor: "text.primary",
        ml: "1px",
        verticalAlign: "text-bottom",
        animation: "streamCursorBlink 1s step-end infinite",
        "@keyframes streamCursorBlink": {
          "0%, 100%": { opacity: 1 },
          "50%": { opacity: 0 },
        },
      }}
    />
  ) : null;

  if (grouped) {
    return (
      <Box sx={{ ...messageRowSx, py: 0.4 }}>
        <Box sx={{ pl: TEXT_INDENT }}>
          <MarkdownContent
            sx={{
              fontSize: "14px",
              color: "text.primary",
              lineHeight: 1.6,
              wordBreak: "break-word",
            }}
          >
            {displayContent}
          </MarkdownContent>
          {streamingCursor}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ ...messageRowSx, pt: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <AgentAvatar name={name} size={AVATAR_SIZE} onClick={avatarClick} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "text.primary",
                lineHeight: 1.4,
              }}
            >
              {name}
            </Typography>
            {timestamp && !isStreaming && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.disabled",
                  lineHeight: 1.4,
                  flexShrink: 0,
                }}
              >
                {formatMessageTime(timestamp)}
              </Typography>
            )}
          </Box>
          <MarkdownContent
            sx={{
              fontSize: "14px",
              color: "text.primary",
              lineHeight: 1.6,
              wordBreak: "break-word",
              mt: 0.25,
            }}
          >
            {displayContent}
          </MarkdownContent>
          {streamingCursor}
        </Box>
      </Box>
    </Box>
  );
}

export function parseAgentOutputJson(text) {
  if (!text || typeof text !== "string") return null;
  const trimmed = text.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && Array.isArray(parsed.output) && parsed.output.length > 0) {
      const placeholders = parsed.output.filter(
        (p) => p.placeholder && p.content && !p.placeholder.startsWith("chatSessionId")
      );
      if (placeholders.length === 0) return null;
      return { placeholders, explanation: parsed.explanation || null };
    }
  } catch {
    // not JSON
  }
  return null;
}

export function AgentOutputCard({
  content,
  agentName,
  agentId,
  timestamp,
  grouped,
  onOpenAgentSettings,
  workflowSessionId,
  projectId,
  projectSessionMode,
}) {
  const name = agentName || "Agent";
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  const parsed = useMemo(() => parseAgentOutputJson(content), [content]);

  const sessionHref = useMemo(() => {
    if (!projectId || !workflowSessionId) return null;
    const base = `/project/logs/progress_view/${projectId}/${workflowSessionId}`;
    return projectSessionMode === "TEST" ? `${base}?test=true` : base;
  }, [projectId, workflowSessionId, projectSessionMode]);

  if (!parsed) return null;

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar name={name} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: "action.hover",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <OutputOutlinedIcon sx={{ fontSize: 15, color: "primary.main" }} />
            <Typography
              sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary", flex: 1 }}
            >
              Agent output
            </Typography>
            {sessionHref && (
              <Box
                component="a"
                href={sessionHref}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "primary.main",
                  textDecoration: "none",
                  flexShrink: 0,
                  transition: "all 100ms ease",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    borderColor: "divider",
                  },
                }}
              >
                View session
                <OpenInNewIcon sx={{ fontSize: 11 }} />
              </Box>
            )}
            {timestamp && (
              <Typography sx={{ fontSize: "11px", color: "text.secondary", flexShrink: 0 }}>
                {formatMessageTime(timestamp)}
              </Typography>
            )}
          </Box>

          <Box sx={{ px: 1.5, py: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {parsed.placeholders.map((ph, i) => (
              <Box key={ph.placeholder || i}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    mb: 0.5,
                  }}
                >
                  {ph.placeholder}
                </Typography>
                <MarkdownContent
                  sx={{
                    fontSize: "13.5px",
                    color: "text.primary",
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                  }}
                >
                  {ph.content}
                </MarkdownContent>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function SupervisorDecisionMessage({
  content,
  agentName,
  agentId,
  timestamp,
  onOpenAgentSettings,
}) {
  const name = agentName || "Supervisor";
  const parsed = parseSupervisorDecisionContent(content);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  const _sp = useStatusPalette();
  const SUPERVISOR_DECISION_CONFIG = buildSupervisorDecisionConfig(_sp);
  const decision = parsed?.decision || null;
  const cfg = decision ? SUPERVISOR_DECISION_CONFIG[decision] : null;
  const roundLabel = parsed?.round ? `Round ${parsed.round}` : null;
  const feedback = parsed?.feedback || "";
  const actions = Array.isArray(parsed?.actions) ? parsed.actions : [];
  const takeoverResponse = parsed?.takeoverResponse || "";
  const summaryByDecision = {
    APPROVE: "Supervisor approved the worker response.",
    REVISE: "Supervisor requested revisions before responding.",
    TAKEOVER: "Supervisor took over and responded directly.",
  };
  const summary = decision
    ? summaryByDecision[decision]
    : "Supervisor intervention recorded.";

  return (
    <Box sx={{ ...messageRowSx, py: 0.5, pt: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <AgentAvatar name={name} size={AVATAR_SIZE} onClick={avatarClick} />
        <Box
          sx={{
            border: `1px solid ${cfg?.border || _sp.blue.border}`,
            borderLeft: `4px solid ${cfg?.color || _sp.blue.color}`,
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "260px",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              backgroundColor: cfg?.bg || _sp.blue.bg,
              borderBottom: `1px solid ${cfg?.border || _sp.blue.border}`,
            }}
          >
            <SupervisorAccountOutlinedIcon
              sx={{ fontSize: 15, color: cfg?.color || _sp.blue.color }}
            />
            <Typography
              sx={{ fontSize: "13px", fontWeight: 700, color: "text.primary" }}
            >
              Supervisor decision
            </Typography>
            {roundLabel && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: "18px",
                  px: "6px",
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "text.secondary",
                    lineHeight: 1,
                  }}
                >
                  {roundLabel}
                </Typography>
              </Box>
            )}
            {cfg && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: "18px",
                  px: "6px",
                  borderRadius: "4px",
                  backgroundColor: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: cfg.color,
                    lineHeight: 1,
                  }}
                >
                  {cfg.label}
                </Typography>
              </Box>
            )}
            <Box sx={{ flex: 1 }} />
            {timestamp && (
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "text.secondary",
                  lineHeight: 1.2,
                  flexShrink: 0,
                }}
              >
                {formatMessageTime(timestamp)}
              </Typography>
            )}
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                fontWeight: 600,
                mb: 0.6,
              }}
            >
              {summary}
            </Typography>
            {feedback ? (
              <>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 0.4,
                  }}
                >
                  Feedback to worker
                </Typography>
                <MarkdownContent
                  sx={{
                    fontSize: "13px",
                    color: "text.primary",
                    lineHeight: 1.55,
                    wordBreak: "break-word",
                  }}
                >
                  {feedback}
                </MarkdownContent>
              </>
            ) : null}
            {actions.length > 0 && (
              <Box sx={{ mt: feedback ? 1 : 0.5 }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 0.5,
                  }}
                >
                  Actions executed
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {actions.map((action) => (
                    <Chip
                      key={action}
                      label={action}
                      size="small"
                      sx={{
                        height: 20,
                        borderRadius: "6px",
                        fontSize: "10px",
                        fontFamily: "monospace",
                        backgroundColor: "action.hover",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {takeoverResponse && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 0.4,
                  }}
                >
                  Takeover response
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "6px",
                    backgroundColor: "action.hover",
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <MarkdownContent
                    sx={{
                      fontSize: "13px",
                      color: "text.primary",
                      lineHeight: 1.55,
                      wordBreak: "break-word",
                    }}
                  >
                    {takeoverResponse}
                  </MarkdownContent>
                </Box>
              </Box>
            )}
            {!feedback && actions.length === 0 && !takeoverResponse && (
              <MarkdownContent
                sx={{
                  fontSize: "13px",
                  color: "text.primary",
                  lineHeight: 1.55,
                  wordBreak: "break-word",
                }}
              >
                {content}
              </MarkdownContent>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
